import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { WordSet } from '@/content/words';
import { useWakeLock } from '@/lib/wakeLock';
import { qa } from '@/qa/recorder';
import { createPracticeSession, type SessionOptions } from './createPracticeSession';

const MIC_ON = new Set(['starting', 'listening', 'hearing', 'checking', 'talking']);

/** Owns the screen's practice session for its lifetime and exposes its live snapshot. */
export function useScannerSession(wordSet: WordSet, options: SessionOptions) {
  const [setup] = useState(() => createPracticeSession(wordSet, options));
  const simulated = options.simulatedMic;
  const { session } = setup;
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const disposeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(disposeTimer.current);
    // Simulated mode is for development and tests: expose the session for inspection.
    if (simulated)
      Object.assign(window, {
        foeSession: session,
        foeSimulatedMic: setup.simulatedMic,
        foeQa: qa,
      });
    void session.load();
    // Switch the microphone off when the page is hidden (another tab, the app in the background).
    const onVisibility = () => {
      if (document.hidden) void session.stopMic();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      void session.stopMic();
      // Free the worker and model when the screen goes away. Deferred, because React's
      // StrictMode unmounts and remounts once in development; the remount cancels this.
      disposeTimer.current = setTimeout(() => {
        session.dispose();
      }, 0);
    };
  }, [session, simulated, setup.simulatedMic]);

  // The screen stays on while the microphone is on (practice in progress), and may sleep once it
  // is off (tapped off, or the inactivity pause).
  useWakeLock(MIC_ON.has(snapshot.phase));

  return { snapshot, session, simulatedMic: setup.simulatedMic };
}
